/** API do ambiente, derivada do host (o mesmo build roda em staging e produção). */
export function apiBaseUrl(hostname = typeof location !== 'undefined' ? location.hostname : 'localhost'): string {
  if (hostname === 'app.licita.dev') return 'https://api.licita.dev';
  if (hostname === 'staging-app.licita.dev') return 'https://staging-api.licita.dev';
  return import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/** 403 SEM_EMPRESA: a conta existe, mas a equipe ainda não ligou a pessoa a uma empresa. */
export const semEmpresa = (e: unknown) => e instanceof ApiError && e.status === 403 && e.code === 'SEM_EMPRESA';

// ---------- empresa ativa ----------

const EMPRESA_KEY = 'licita.empresa';
let empresaAtiva: string | null = null;

/** Id da empresa ativa (memória, com cópia em localStorage para sobreviver ao reload). */
export function getEmpresaAtiva(): string | null {
  if (empresaAtiva === null) {
    try {
      empresaAtiva = localStorage.getItem(EMPRESA_KEY);
    } catch {}
  }
  return empresaAtiva;
}

export function setEmpresaAtiva(id: string | null): void {
  empresaAtiva = id;
  try {
    if (id) localStorage.setItem(EMPRESA_KEY, id);
    else localStorage.removeItem(EMPRESA_KEY);
  } catch {}
}

/** Header `x-empresa` em toda chamada /app/*; sem ele a API usa a primeira empresa da pessoa. */
const empresaHeader = (): Record<string, string> => {
  const id = getEmpresaAtiva();
  return id ? { 'x-empresa': id } : {};
};

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(`${apiBaseUrl()}${path}`, {
    credentials: 'include',
    ...rest,
    headers: { ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...empresaHeader(), ...rest.headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  return lerResposta<T>(res);
}

/** Upload bruto (application/octet-stream) com o nome no header `x-nome`; o `api()` só fala JSON. */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const res = await fetch(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/octet-stream', 'x-nome': encodeURIComponent(file.name), ...empresaHeader() },
    body: file,
  });
  return lerResposta<T>(res);
}

async function lerResposta<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, body.error ?? 'ERRO', body.message ?? 'Não foi possível concluir.');
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}
